import { ILayerWrapperOptions, LayerWrapper } from '../LayerWrapper';
import { Events } from '@shared/Events';
import { Ajax } from '@shared/Ajax';
import { AlignmentFeatureFactory } from './AlignmentFeatureFactory';
import { AlignmentStationsLayer } from './AlignmentStationsLayer';
import { colorToRGB } from './AlignmentFeatureFactory';
import Feature from 'ol/Feature';
import { AlignmentStore } from '@shared/alignment/AlignmentStore';

export class AlignmentsLayer extends LayerWrapper<IAlignment> {
    featureType: FeatureType = 'alignment';
    activeAlignmentFeature: Feature = null;
    fetchPromise: JQueryPromise<any> = null;

    constructor(opts: ILayerWrapperOptions<IAlignment>, private alignmentStore: AlignmentStore) {
        super(opts);
    }

    public setActiveAlignmentById(id: number | string, index: number) {
        this.clearStyles();
        AlignmentStationsLayer.clear();
        this.activeAlignmentFeature = null;

        const targetFeature: Feature = this.getFeatureById(id + '_' + index);

        if (targetFeature) {
            this.setActiveAlignment(targetFeature);
        } else {
            // alignment isn't loaded yet, fetch it
            if (id == -1 || id == '' || this.fetchPromise) return false;

            this.fetchPromise = this.fetchAndDrawFile(id)
                .done(() => {
                    const feature: Feature = this.getFeatureById(id + '_' + index);
                    this.setActiveAlignment(feature);
                })
                .fail(() => {
                    this.setActiveAlignment(-1);
                })
                .always(() => {
                    this.fetchPromise = null;
                });

            return false;
        }
    }

    private setActiveAlignment(feature: Feature | number) {
        if (!feature || typeof feature === 'number') return;

        this.activeAlignmentFeature = feature;
        const data = this.getAjaxData(feature);
        this.drawStations(data);
        feature.setStyle([AlignmentFeatureFactory.styles.hitArea, AlignmentFeatureFactory.styles.selected(colorToRGB(data.color))]);

        Events.trigger('activeAlignmentChange', {
            // todo adapt to new id {}
            id: this.getActiveAlignment(),
            feature,
        });
    }

    getActiveAlignment = (): { modelId: number; index: number } => {
        if (!this.activeAlignmentFeature) {
            return {
                modelId: -1,
                index: 0,
            };
        }

        const ajaxData = this.getAjaxData(this.activeAlignmentFeature);
        return {
            modelId: ajaxData.modelId,
            index: ajaxData.index,
        };
    };

    fetchAndDrawFile(ajaxData: any): JQueryPromise<any> {
        return this.fetchAlignmentById(ajaxData).then(
            resp => {
                // First pass: determine which models are legacy
                const modelLegacyStatus = new Map<number, boolean>();

                resp.alignments.forEach(alignment => {
                    if (alignment.name == undefined) {
                        modelLegacyStatus.set(alignment.modelId, true);
                    } else if (!modelLegacyStatus.has(alignment.modelId)) {
                        modelLegacyStatus.set(alignment.modelId, false);
                    }
                });

                // Set legacy status for each model
                modelLegacyStatus.forEach((isLegacy, modelId) => {
                    this.alignmentStore.setModelLegacy(modelId, isLegacy);
                });

                // Second pass: create keys based on legacy status
                resp.alignments.forEach(alignment => {
                    const isLegacy = modelLegacyStatus.get(alignment.modelId);
                    let key: string;

                    if (isLegacy) {
                        // Legacy: simple index-based key
                        key = alignment.index === 0 ? `${alignment.modelId}` : `${alignment.modelId}_${alignment.index}`;
                    } else {
                        // New: base64-encoded name-based key
                        const base64Name = alignment.name ? btoa(encodeURIComponent(alignment.name)) : btoa(`index_${alignment.index}`);
                        key = `${alignment.modelId}_${base64Name}`;
                    }

                    this.alignmentStore.alignmentIndexes.set(key, alignment.index);
                });

                const feature = this.drawAll(resp.alignments);
                return feature ? $.Deferred().resolve(resp) : $.Deferred().reject(resp);
            },
            resp => $.Deferred().reject(resp, status, 'ajax error')
        );
    }

    removeFeaturesBySource(source: FeatureSource) {
        super.removeFeaturesBySource(source);

        if (source.modelId != null) {
            const prefix = `${source.modelId}_`;
            const exactKey = `${source.modelId}`;
            const legacyKey = `__legacy_${source.modelId}__`;
            for (const key of Array.from<string>(this.alignmentStore.alignmentIndexes.keys())) {
                if (key === exactKey || key === legacyKey || key.startsWith(prefix)) {
                    this.alignmentStore.alignmentIndexes.delete(key);
                }
            }
        }

        // check if active alignment was removed (unchecked) and notify
        if (this.activeAlignmentFeature) {
            // expect to have active alignment
            const found = this.layerSource.getFeatureById(this.activeAlignmentFeature.getId());
            if (!found) {
                // but if was removed
                this.activeAlignmentFeature = null;
                Events.trigger('active-alignment-feature:removed');
            }
        }
    }

    onMapZoom(zoomLevel: number) {
        if (this.activeAlignmentFeature) {
            const data = this.activeAlignmentFeature.get('data');
            this.drawStations(data);
        }
    }

    drawStations = (data: IAlignment) => {
        AlignmentStationsLayer.drawOnMapForAlignment(data, this.map, 49);
    };

    private fetchAlignmentById(alignmentId: number | 'undefined'): JQueryPromise<any> {
        if (alignmentId == undefined || alignmentId == 'undefined' || alignmentId < 0) {
            return $.Deferred().reject();
        }

        return Ajax({
            url: 'ajax_alignments.json',
            data: {
                id: alignmentId,
            },
        });
    }
}
